import React, { useState } from "react"
import { DesenhoEixos, MAX_EIXOS, totalPneus, estepesTxt } from "../utils/reboques"

// Editor do desenho dos eixos de um modelo de reboque.
//  Modo simples: quantidade de eixos + pneus por eixo (todos iguais) + estepes. 4 pneus = dois de cada lado (rodado duplo); 2 pneus = um de cada lado.
//  Modo detalhado: eixo por eixo, para reboques com eixos diferentes (ex.: um duplo e um simples).
// A previa desenha o resultado. `rotulo` distingue varios editores na mesma tela (rotulos de acessibilidade).
export default function EixosReboque({ valor, onChange, rotulo, desabilitado }: { valor: DesenhoEixos; onChange: (d: DesenhoEixos) => void; rotulo?: string; desabilitado?: boolean }) {
  const sufixo = rotulo ? ` (${rotulo})` : ""
  const misto = new Set(valor.pneus).size > 1
  const [detalhado, setDetalhado] = useState(misto)
  const [base, setBase] = useState<2 | 4>((valor.pneus[0] as 2 | 4) || 4)   // pneus por eixo escolhido no modo simples (vale tambem enquanto nao ha eixos)
  const mostrarDetalhe = detalhado || misto
  const n = valor.pneus.length
  const total = totalPneus(valor.pneus)

  const mudarQtd = (txt: string) => {
    const q = Math.min(parseInt(txt, 10) || 0, MAX_EIXOS)
    onChange({ ...valor, pneus: q < 1 ? [] : Array.from({ length: q }, () => base) })
  }
  const mudarBase = (p: 2 | 4) => { setBase(p); onChange({ ...valor, pneus: valor.pneus.map(() => p) }) }
  const mudarEixo = (i: number, p: number) => onChange({ ...valor, pneus: valor.pneus.map((x, k) => (k === i ? p : x)) })
  const addEixo = () => valor.pneus.length < MAX_EIXOS && onChange({ ...valor, pneus: [...valor.pneus, base] })
  const tirarEixo = (i: number) => valor.pneus.length > 1 && onChange({ ...valor, pneus: valor.pneus.filter((_, k) => k !== i) })
  const estepes = (
    <div className="form-group"><label>Estepes</label>
      <select aria-label={`Estepes${sufixo}`} value={valor.estepes} disabled={desabilitado} onChange={(e) => onChange({ ...valor, estepes: Number(e.target.value) })}>
        <option value={0}>Sem estepe</option><option value={1}>1 estepe</option><option value={2}>2 estepes</option>
      </select></div>
  )

  return (
    <div role="group" aria-label={`Eixos do reboque${sufixo}`} style={{ border: "1px solid #2d4166", borderRadius: 10, padding: 12, marginTop: 8, width: "100%", boxSizing: "border-box" }}>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          {!mostrarDetalhe ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="form-group"><label>Quantidade de eixos</label>
                <input aria-label={`Quantidade de eixos${sufixo}`} type="number" min={1} max={MAX_EIXOS} placeholder="Ex.: 2" value={n || ""} disabled={desabilitado} onChange={(e) => mudarQtd(e.target.value)} /></div>
              <div className="form-group"><label>Pneus por eixo</label>
                <select aria-label={`Pneus por eixo${sufixo}`} value={base} disabled={desabilitado} onChange={(e) => mudarBase(Number(e.target.value) as 2 | 4)}>
                  <option value={4}>4 pneus (2 de cada lado)</option><option value={2}>2 pneus (1 de cada lado)</option>
                </select></div>
              {estepes}
            </div>
          ) : (
            <div>
              {valor.pneus.map((p, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                  <b style={{ minWidth: 54, fontSize: 13 }}>Eixo {i + 1}</b>
                  <select aria-label={`Pneus do eixo ${i + 1}${sufixo}`} value={p} disabled={desabilitado} onChange={(e) => mudarEixo(i, Number(e.target.value))} style={{ flex: 1 }}>
                    <option value={4}>4 pneus (2 de cada lado)</option><option value={2}>2 pneus (1 de cada lado)</option>
                  </select>
                  <button type="button" className="btn btn-ghost" style={{ padding: "2px 8px" }} aria-label={`Remover eixo ${i + 1}${sufixo}`} disabled={desabilitado || n <= 1} onClick={() => tirarEixo(i)}>✕</button>
                </div>
              ))}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                <button type="button" className="btn btn-ghost" disabled={desabilitado || n >= MAX_EIXOS} onClick={addEixo}>+ Adicionar eixo</button>
                <div style={{ minWidth: 150 }}>{estepes}</div>
              </div>
            </div>
          )}
          <p style={{ margin: "10px 0 0", fontSize: 13, color: n ? "#f1f5f9" : "#94a3b8" }} aria-live="polite">
            {n ? <>Total: <b>{total} pneus</b> em {n} {n === 1 ? "eixo" : "eixos"}{misto ? "" : ` (${valor.pneus[0] / 2} de cada lado por eixo)`} · {estepesTxt(valor.estepes)}</> : "Informe a quantidade de eixos."}
          </p>
          <button type="button" className="btn btn-ghost" style={{ marginTop: 8, fontSize: 12, padding: "4px 10px" }} disabled={desabilitado || (mostrarDetalhe && misto)}
            title={mostrarDetalhe && misto ? "Os eixos são diferentes entre si: use o modo detalhado" : undefined} onClick={() => setDetalhado(!mostrarDetalhe)}>
            {mostrarDetalhe ? "Voltar ao modo simples" : "Detalhar eixo por eixo"}
          </button>
        </div>
        <Previa valor={valor} />
      </div>
    </div>
  )
}

// desenho do reboque visto de cima: eixos de cima (frente) para baixo; pneus como retangulos; estepes no fim
function Previa({ valor }: { valor: DesenhoEixos }) {
  const n = valor.pneus.length, h = 22, topo = 8, largura = 150, meio = largura / 2
  const altura = topo + Math.max(n, 1) * h + (valor.estepes ? 22 : 0) + 6
  const pneu = (x: number, y: number, k: string) => <rect key={k} x={x} y={y} width={9} height={15} rx={2} fill="currentColor" opacity={0.85} />
  return (
    <svg role="img" aria-label={n ? `Prévia: ${valor.pneus.map((p, i) => `eixo ${i + 1} com ${p} pneus`).join(", ")}, ${estepesTxt(valor.estepes)}` : "Prévia vazia"} width={largura} height={altura} viewBox={`0 0 ${largura} ${altura}`} style={{ color: "#94a3b8", flexShrink: 0 }}>
      <line x1={meio} y1={2} x2={meio} y2={topo + n * h - 6} stroke="currentColor" strokeWidth={2} opacity={0.4} />
      {valor.pneus.map((p, i) => {
        const y = topo + i * h, porLado = p / 2
        return (
          <g key={i}>
            <line x1={meio - 38} y1={y + 7.5} x2={meio + 38} y2={y + 7.5} stroke="currentColor" strokeWidth={2} />
            {Array.from({ length: porLado }, (_, k) => pneu(meio - 47 - k * 11, y, `e${k}`))}
            {Array.from({ length: porLado }, (_, k) => pneu(meio + 38 + k * 11, y, `d${k}`))}
          </g>
        )
      })}
      {Array.from({ length: valor.estepes }, (_, k) => <circle key={k} cx={meio - 12 + k * 24} cy={topo + n * h + 8} r={8} fill="none" stroke="currentColor" strokeWidth={2} />)}
    </svg>
  )
}
